// Third-party
import {
  DataGridPro, GridApiPro, GridColDef, useGridApiRef,
} from '@mui/x-data-grid-pro';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

// Components
import {
  MutableRefObject, useCallback, useRef, useState,
} from 'react';
import { useTheme } from '@mui/material';
import Card from '@mui/material/Card';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { dateFormatTZ, formatDecimalHours } from '@mgm/common/utils';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { TableActions } from '../../../../../components/table/TableActions';
import { TimesheetsMemberTableHeader } from './TimesheetsMemberTableHeader';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../../../hooks/useAppStore';

// Redux-toolkit
import { getSelectedMember } from '../../../../../store/sheets/selectors';
import { getCurrencyById, getIsPrintMode } from '../../../../../store/common/selectors';
import { sheetsActions } from '../../../../../store/sheets/slice';
import { commonActions } from '../../../../../store/common/slice';
import { useTable } from '../../../../../hooks/useTable';
import { useSheetsPermissions } from '../../../hooks';
import { PrintWrapper } from '../../../../../layout/wrapper/PrintWrapper';

const TimesheetsMemberTable = () => {
  const { t } = useTranslation(['timesheets', 'inputs', 'common']);
  const selectedMember = useAppSelector(getSelectedMember);
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const { localText, handleScrollTable, handleExport } = useTable(apiRef);
  const getCurrency = useAppSelector(getCurrencyById);
  const dispatch = useAppDispatch();
  const { permissions } = useSheetsPermissions();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const isPrintMode = useAppSelector(getIsPrintMode);

  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const [isFullTable, setIsFullTable] = useState(false);

  const baseColumns: Array<GridColDef> = [
    {
      field: 'start',
      headerName: t('date'),
      width: 250,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {dateFormatTZ(row.start, 'DD MMMM, hh:mm A')}
            {' '}
            -
            {dateFormatTZ(row.end, 'hh:mm A')}
          </Typography>
        </Box>
      ),
      valueFormatter: (_, row) => `${dateFormatTZ(row.start, 'DD MMMM, hh:mm A')} - ${dateFormatTZ(row.end, 'hh:mm A')}`,
      valueGetter: (_, row) => row.start,
    },
    {
      field: 'wage',
      headerName: t('wage', { ns: 'inputs' }),
      width: 130,
      renderCell: () => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {selectedMember.payment?.paymentAmount}
          </Typography>
        </Box>
      ),
      valueGetter: () => selectedMember.payment?.paymentAmount,
    },
    {
      field: 'location',
      headerName: t('location', { ns: 'inputs' }),
      width: 130,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.location.name}
          </Typography>
        </Box>
      ),
      valueGetter: (_, row) => row.location.name,
    },
    {
      field: 'hours',
      headerName: t('workTime'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {formatDecimalHours(row.hours)}
          </Typography>
        </Box>
      ),
      valueGetter: (_, row) => formatDecimalHours(row.hours),
    },
    {
      field: 'breakTime',
      headerName: t('breakTime'),
      minWidth: 130,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {moment()
              .startOf('day')
              .second(row.breakTime)
              .format('H:mm')}
          </Typography>
        </Box>
      ),
      valueGetter: (_, row) => row.breakTime,
    },
    {
      field: 'amount',
      headerName: t('amount'),
      minWidth: 130,
      renderCell: ({ row }) => {
        if (row.amount === '-') {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="caption">
                /
              </Typography>
            </Box>
          );
        }

        return (
          <Box display="flex" alignItems="center" height="100%">
            {permissions.isAllowViewSalaryData ? (
              <Typography variant="caption">
                {getCurrency(selectedMember.personnel.paymentCurrencyId).mark}
                {row.amount}
              </Typography>
            ) : <Typography variant="caption">/</Typography>}
          </Box>
        );
      },
      valueGetter: (_, row) => {
        if (row.amount === '-') return '/';

        return row.amount;
      },
    },
  ];

  if (permissions.isAllowManage) {
    baseColumns.push({
      field: 'actions',
      headerName: t('actions', { ns: 'common' }),
      minWidth: 130,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      disableExport: true,
      renderCell: ({ row }) => {
        const openDeleteShiftDialog = () => {
          dispatch(sheetsActions.setToggleDeleteShift(true));
          dispatch(sheetsActions.setSelectedShift(row));
        };

        const openShiftNav = () => {
          dispatch(sheetsActions.setSelectedShift(row));

          setTimeout(() => {
            dispatch(commonActions.setIsOpenPagesNav(true));
          }, 100);
        };

        return (
          <TableActions
            onConfirm={openShiftNav}
            onDelete={openDeleteShiftDialog}
          />
        );
      },

    });
  }

  const timesheetsMemberColumns: Array<GridColDef> = baseColumns;

  const renderTimesheetsMemberHeader = useCallback(() => {
    const userName = selectedMember.personnel.name.replace(' ', '-');

    return (
      <TimesheetsMemberTableHeader
        handleScrollTable={handleScrollTable}
        printRef={componentRef}
        exportFileName={`${userName}-sheets-card`}
        apiRef={apiRef}
        setIsFullTable={setIsFullTable}
        handleExport={() => handleExport(`${userName}-sheets-card`)}
      />
    );
  }, [apiRef]);

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoShifts')} />;

  return (
    <PrintWrapper componentRef={componentRef}>
      <Card
        sx={{
          height: isFullTable && selectedMember.shifts?.length > 0 ? 'auto' : 550,
          p: 0,
          ...(isMobile && {
            background: 'unset', boxShadow: 'unset', px: 0, pt: 0, pb: '35px',
          }),
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          rows={selectedMember.shifts}
          localeText={localText}
          sx={{
            '.MuiDataGrid-main > *:first-of-type': {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            },
          }}
          slots={{
            toolbar: renderTimesheetsMemberHeader,
            noRowsOverlay: renderCustomNoRowsOverlay,
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'start', sort: 'desc' }],
            },
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              printOptions: {
                fileName: `${selectedMember.personnel.name}-sheets-card`,
                hideFooter: true,
                hideToolbar: true,
              },
            },
          }}
          showToolbar
          getRowId={(row) => row.id}
          columns={timesheetsMemberColumns}
          hideFooterPagination
          disableRowSelectionOnClick
          hideFooter
          checkboxSelection={false}
          className={isPrintMode ? 'print-mode' : ''}
        />
      </Card>
    </PrintWrapper>
  );
};

export {
  TimesheetsMemberTable,
};
